import { Divider } from "@affinity/components/Divider";
import { FLOALA_TOOLBAR_COLLAPSED, FLOALA_TOOLBAR_EXPANDED, FROALA_CONFIG } from "@affinity/constants/froalaConfig";
import { rem } from "@affinity/constants/StyleHelpers";
import UpdateAccountPreferences from "@affinity/Handlers/UpdateAccountPreferences";
import { RichContentComponent } from "@affinity/Overrides/Widgets/Basic/RichContent";
import { BuildPersitenceUrl, getQueryStringFromState } from "@affinity/Store/PrePopulation";
import { CustomState } from "@affinity/Store/Reducers";
import { defaultBillToModel } from "@affinity/Store/StoreTypeStates";
import { extractStylesToArray, isSafe } from "@insite/client-framework/Common/Utilities/isSafeStyles";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: CustomState) => ({
    // Map required state elements to component properties
    currentAccount: state.affinity_create_account.billTo,
    placeOrderUrl: getPageLinkByPageType(state, "AffinityPlaceOrderPage")?.url,
    queryStringState: getQueryStringFromState(state),
});

const mapDispatchToProps = {
    // Map loader and setter methods to component properties
    UpdateAccountPreferences,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> & {
        fields: {
            marketingConsentText: string;
            termsAndConditionsText: string;
        };
    } &
    HasHistory;

export const Step3Preferences: React.FC<Props> = ({ 
    currentAccount, 
    UpdateAccountPreferences, 
    fields,
    placeOrderUrl,
    queryStringState,
}) => {
    function submit(data: Object) {
        if (currentAccount) {
            UpdateAccountPreferences({
                billToId: currentAccount.id,
                confirmedMarketing: false,
                confirmedTermsAndConditions: false,
                ...data,
                onComplete: () => {
                    if (placeOrderUrl && queryStringState) {
                        window.location.href = BuildPersitenceUrl(placeOrderUrl, queryStringState);
                    }
                }
            });
        }
    }

    const [formData, setFormData] = useState({
        confirmedMarketing: false,
        confirmedTermsAndConditions: false,
    });

    const handleSubmit = (e: any) => {
        e.preventDefault();
        submit(formData);
    };

    return (
        <StyledForm onSubmit={handleSubmit}>
            <FieldGroup>
                <Typography variant="h3">Permission to contact you</Typography>
                <Checkbox
                    checked={formData.confirmedMarketing}
                    onChange={() => {
                        const newFormData = {
                            ...formData,
                            confirmedMarketing: !formData.confirmedMarketing,
                        };
                        setFormData(newFormData);
                    }}
                >
                    <RichContentComponent fields={{ content: fields.marketingConsentText }} css={undefined} />
                </Checkbox>
            </FieldGroup>
            <Divider />
            <Button
                type="submit"
                variant="primary"
                sizeVariant="large"
                icon={{ position: "right", src: "ChevronRight" }}
                css={css`
                    align-self: end;
                `}
            >
                Create account
            </Button>
        </StyledForm>
    );
};
Step3Preferences.displayName = "Create Account Step 3 - Preferences";

const CreateAccountStep3Preferences = connect(mapStateToProps, mapDispatchToProps)(withHistory(Step3Preferences));

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(10)};
`;

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: ${rem(20)};
    gap: ${rem(30)};
`;

export const definition: WidgetDefinition = {
    group: "Create Account",
    fieldDefinitions: [
        {
            fieldType: "General",
            name: "marketingConsentText",
            editorTemplate: "RichTextField",
            displayName: "Marketing consent text",
            defaultValue: "",
            isRequired: true,
            validate: (html: string) => {
                const arrayOfStyles = extractStylesToArray(html);
                return arrayOfStyles?.every(style => isSafe(style))
                    ? null
                    : "The HTML contains invalid styles that prevent it from being saved";
            },
            extendedConfig: {
                ...FROALA_CONFIG,
            },
            collapsedToolbarButtons: FLOALA_TOOLBAR_COLLAPSED,
            expandedToolbarButtons: FLOALA_TOOLBAR_EXPANDED,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: CreateAccountStep3Preferences,
    definition,
};

export default widgetModule;
